import { TextField } from '@mui/material';
import { KycRiskRating } from 'further-types/kyc';
import { kycRiskRatingLabels } from 'further-ui/labels';
import BasicSelect from 'further-ui/components/BasicSelect';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import FileDrop from 'further-ui/components/FileDrop';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import { Controller, useForm } from 'react-hook-form';
import { UploadType } from 'further-types/files';
import useFileUpload from 'hooks/ui/useFileUpload';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getMaxSizeForUploadType,
  getValidFileTypesForUploadType,
} from 'further-ui/config';
import { compact, round } from 'lodash';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

const formSchema = z.object({
  riskRating: z.nativeEnum(KycRiskRating),
  manualApprovalNotes: z.string().optional(),
  manualApprovalFiles: z.array(
    z.object({
      path: z.string(),
      name: z.string(),
    }),
  ),
});

type Props = {
  title: string;
  vehicleId: string;
  investorId: string;
  investorKyc?: {
    manualApprovalNotes?: string;
    riskRating?: KycRiskRating;
    manualApprovalFiles?: Array<{
      path: string;
      name: string;
      previewUrl: string;
    }>;
  };
  onClose: () => void;
};

type FormValues = z.infer<typeof formSchema>;

const ManualKycDetailsForm: React.FC<Props> = ({
  vehicleId,

  investorId,
  investorKyc,

  title,
  onClose,
}) => {
  const { uploadFile } = useFileUpload();
  const { error: showError } = useNotification();

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      riskRating: investorKyc?.riskRating ?? KycRiskRating.Low,
      manualApprovalNotes: investorKyc?.manualApprovalNotes ?? '',
      manualApprovalFiles: investorKyc?.manualApprovalFiles ?? [],
    },
  });

  const { isPending, mutate } = api.lpKyc.saveManualKycDetails.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Manual KYC record saved successfully.',
      invalidations: [
        getQueryKey(api.lpKyc.getInvestorKyc, { vehicleId, investorId }),
      ],
    }),
  );

  const onSubmit = (values: FormValues) => {
    if (isPending) return;

    mutate({
      vehicleId,
      investorId,
      ...values,
    });
    onClose();
  };

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      maxWidth="sm"
      content={
        <Column fullWidth spacing="lg">
          <FormField
            label="Risk rating"
            extraInformation="Selecting a rating will override the system default rating for this investor."
          >
            <Controller
              control={control}
              name="riskRating"
              render={({ field, fieldState: { error } }) => (
                <BasicSelect
                  size="small"
                  {...field}
                  fullWidth
                  options={kycRiskRatingLabels}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField label="Notes">
            <Controller
              control={control}
              name="manualApprovalNotes"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter manual approval notes"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>

          <FormField label="Documents">
            <Controller
              control={control}
              name="manualApprovalFiles"
              render={({ field: { value, onChange } }) => (
                <FileDrop
                  inputTitle="Admin-uploaded files"
                  maxFiles={10}
                  files={value.map((document) => ({
                    fileName: document.name,
                    previewUrl: document.path,
                    isUploading: !document.path,
                  }))}
                  onDeleteFile={(index) => {
                    onChange(value.filter((_, i) => i !== index));
                  }}
                  onDropFile={async (files) => {
                    const handleFileUpload = async (file: File) => {
                      const { data, error } = await uploadFile(
                        file,
                        UploadType.KycDocuments,
                      );

                      if (error) {
                        showError(error.message);
                        return null;
                      }

                      return {
                        name: data.name,
                        path: data.filePath,
                      };
                    };

                    onChange([
                      ...value,
                      ...files.map((file) => ({
                        name: file.fileName,
                        path: '',
                      })),
                    ]);

                    const uploadedFiles = compact(
                      await Promise.all(
                        files.map(
                          async (droppedFile) =>
                            await handleFileUpload(droppedFile.fileData),
                        ),
                      ),
                    );

                    onChange([...value, ...uploadedFiles]);
                  }}
                  sizeRestriction={{
                    unit: 'mb',
                    value: round(
                      getMaxSizeForUploadType(UploadType.KycDocuments, 'mb'),
                    ),
                  }}
                  supportedFormats={getValidFileTypesForUploadType(
                    UploadType.KycDocuments,
                  )}
                />
              )}
            />
          </FormField>
        </Column>
      }
      actions={{
        confirm: {
          onClick: () => {
            void handleSubmit(onSubmit)();
          },
          label: 'Save',
        },
        cancel: { onClick: onClose, label: 'Cancel' },
      }}
      title={title}
    />
  );
};

export default ManualKycDetailsForm;
